import { useState } from 'react';
import { X } from 'lucide-react';
import { api } from '../../lib/api';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import { SecretInput } from '../ui/SecretInput';
import { AccountTab } from '../clients/settings/AccountTab';

const EMAIL_VALIDO = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/**
 * "Minha conta": trocar o e-mail de login e a senha. Abre pelo botão Editar ao
 * lado do nome, no canto do menu. Antes a troca de senha ficava dentro das
 * configurações de cada cliente, que não é lugar de dado da pessoa.
 */
export function MinhaConta({ emailAtual, onClose }: { emailAtual: string; onClose: () => void }) {
  const ref = useFocusTrap<HTMLDivElement>({ active: true, onEscape: onClose });
  const [form, setForm] = useState({ novoEmail: '', senha: '' });
  const [erro, setErro] = useState('');
  const [sucesso, setSucesso] = useState('');
  const [salvando, setSalvando] = useState(false);

  const trocarEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    setErro('');
    setSucesso('');
    const novoEmail = form.novoEmail.trim().toLowerCase();
    if (!EMAIL_VALIDO.test(novoEmail)) return setErro('Digite um e-mail válido.');
    if (novoEmail === emailAtual.toLowerCase()) return setErro('O novo e-mail é igual ao atual.');
    if (salvando) return;

    setSalvando(true);
    try {
      await api.changeEmail(form.senha, novoEmail);
      setSucesso('E-mail alterado. Indo para o login para você entrar com o e-mail novo…');
      setTimeout(() => { window.location.href = '/login'; }, 1500);
    } catch (err) {
      setErro(err instanceof Error ? err.message : 'Não deu para trocar o e-mail.');
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className="ds-modal-overlay" onClick={onClose}>
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby="minha-conta-titulo"
        className="ds-modal-content"
        style={{ maxWidth: 440, maxHeight: '90vh', overflowY: 'auto' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-5">
          <h3 id="minha-conta-titulo" style={{ fontFamily: "'Poppins', sans-serif", fontWeight: 600, fontSize: 17, color: 'var(--text)' }}>
            Minha conta
          </h3>
          <button onClick={onClose} aria-label="Fechar minha conta" style={{ color: 'var(--text-dim)' }}>
            <X size={18} aria-hidden="true" />
          </button>
        </div>

        <section aria-labelledby="conta-email-titulo" className="pb-6 mb-6" style={{ borderBottom: '1px solid var(--border)' }}>
          <h4 id="conta-email-titulo" className="ds-micro mb-2">E-mail de login</h4>
          <p className="text-sm mb-4" style={{ color: 'var(--text-dim)' }}>
            Hoje: <span style={{ color: 'var(--text)' }}>{emailAtual}</span>
          </p>

          {erro && (
            <div role="alert" className="rounded-lg p-3 text-sm mb-3" style={{ background: 'var(--danger-soft)', border: '1px solid rgba(248,113,113,0.24)', color: 'var(--danger)' }}>
              {erro}
            </div>
          )}
          {sucesso && (
            <div role="status" className="rounded-lg p-3 text-sm mb-3" style={{ background: 'var(--success-soft)', border: '1px solid rgba(74,222,128,0.24)', color: 'var(--success)' }}>
              {sucesso}
            </div>
          )}

          <form onSubmit={(e) => { void trocarEmail(e); }} className="space-y-3">
            <div>
              <label className="ds-label" htmlFor="conta-email-novo">Novo e-mail</label>
              <input
                id="conta-email-novo"
                type="email"
                autoComplete="email"
                className="ds-input"
                value={form.novoEmail}
                onChange={(e) => setForm({ ...form, novoEmail: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="ds-label" htmlFor="conta-email-senha">Senha atual</label>
              <SecretInput
                id="conta-email-senha"
                autoComplete="current-password"
                value={form.senha}
                onChange={(valor) => setForm({ ...form, senha: valor })}
                required
              />
            </div>
            <button type="submit" disabled={salvando} className="ds-btn ds-btn-primary" style={{ padding: '11px 18px' }}>
              {salvando ? 'Salvando…' : 'Trocar e-mail'}
            </button>
          </form>
        </section>

        <AccountTab />
      </div>
    </div>
  );
}
